import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, FileText, Sparkles, Users, Trash2, Pencil, Briefcase, FolderTree, BookOpen, Archive, Upload, ChevronDown } from "lucide-react";
import { formatIndianDate } from "@/lib/date";
import { getCompany, listResolutions, deleteCompany, downloadCompliancePack, listMcaPrefillForms } from "@/lib/api";
import { toast } from "sonner";
import { McaPrefillModal } from "@/components/McaPrefillModal";
import { computeShareholdingPercents } from "@/lib/shareholding";

// Feature flags — flip to true and redeploy to re-expose these CTAs.
// (File MCA Form + MCA V3 Prefill temporarily hidden per product decision
// on 2026-02-04; underlying wizard/prefill routes still work via direct URL.)
const SHOW_FILE_MCA_AND_PREFILL = false;

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMcaPrefill, setShowMcaPrefill] = useState(false);
  const [mcaForms, setMcaForms] = useState([]);
  const [showFormMenu, setShowFormMenu] = useState(false);
  const formMenuRef = useRef(null);

  useEffect(() => {
    Promise.all([getCompany(id), listResolutions(id)])
      .then(([c, r]) => {
        setCompany(c.data);
        setResolutions(r.data || []);
      })
      .catch(() => toast.error("Failed to load company"))
      .finally(() => setLoading(false));
  }, [id]);

  // Load the list of MCA V3 forms supported by the wizard (10 today). The
  // backend is the single source of truth so this list expands automatically
  // as new builders are registered.
  useEffect(() => {
    listMcaPrefillForms()
      .then((r) => setMcaForms(r.data?.forms || []))
      .catch(() => { /* non-fatal — dropdown just shows nothing */ });
  }, []);

  // Close form-picker on outside click.
  useEffect(() => {
    if (!showFormMenu) return;
    const onDocClick = (e) => {
      if (formMenuRef.current && !formMenuRef.current.contains(e.target)) {
        setShowFormMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showFormMenu]);

  if (loading) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Loading…</div>;
  if (!company) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Not found</div>;

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${company.name} and all its resolutions?`)) return;
    try {
      await deleteCompany(id);
      toast.success("Company deleted");
      navigate("/companies");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="company-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="label-eyebrow">{company.company_type}</span>
            {company.cin && <span className="font-mono-plex text-xs text-zinc-500">{company.cin}</span>}
          </div>
          <h1 className="font-heading text-4xl font-light tracking-tight">{company.name}</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to={`/generate?company=${id}`} className="swiss-btn-primary" data-testid="generate-cta">
            <Sparkles size={16} /> Generate Resolution
          </Link>
          <Link to={`/companies/${id}/fy-docket`} className="swiss-btn-ghost" data-testid="fy-docket-cta">
            <FolderTree size={14} /> FY Docket
          </Link>
          <Link to={`/companies/${id}/registers`} className="swiss-btn-ghost" data-testid="registers-cta">
            <BookOpen size={14} /> Registers
          </Link>
          {/* File MCA Form picker — replaces the old hardcoded "File ADT-1"
              button. Lists every MCA V3 form supported by the backend wizard
              (10 today: ADT-1, AOC-4, MGT-7A, DIR-3 KYC, DPT-3, MGT-14,
              INC-22, INC-20A, DIR-12, MSME-1). */}
          {SHOW_FILE_MCA_AND_PREFILL && (
          <div className="relative" ref={formMenuRef}>
            <button
              type="button"
              onClick={() => setShowFormMenu((v) => !v)}
              className="swiss-btn-ghost flex items-center gap-1.5"
              data-testid="file-mca-form-menu"
              aria-haspopup="menu"
              aria-expanded={showFormMenu}
            >
              <FileText size={14} /> File MCA Form <ChevronDown size={12} />
            </button>
            {showFormMenu && (
              <div
                className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto border border-zinc-200 bg-white shadow-lg z-30"
                data-testid="file-mca-form-menu-list"
                role="menu"
              >
                {mcaForms.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-zinc-500 italic">No forms loaded</div>
                ) : (
                  mcaForms.map((f) => (
                    <Link
                      key={f.key}
                      to={`/companies/${id}/file/${f.key}`}
                      onClick={() => setShowFormMenu(false)}
                      className="block px-3 py-2.5 text-xs hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0"
                      data-testid={`file-form-${f.key}`}
                      role="menuitem"
                    >
                      <div className="font-medium text-zinc-900">{f.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{f.section}</div>
                      <div className="text-[10px] text-[#002FA7] mt-0.5">Due: {f.due}</div>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
          )}
          <button
            onClick={async () => {
              try {
                await downloadCompliancePack(id);
                toast.success("Pre-AGM Pack downloaded");
              } catch {
                toast.error("Could not download pack");
              }
            }}
            className="swiss-btn-ghost"
            data-testid="pre-agm-pack-cta"
            title="One-click ZIP of every statutory register PDF for this company."
          >
            <Archive size={14} /> Pre-AGM Pack
          </button>
          {SHOW_FILE_MCA_AND_PREFILL && (
          <button
            onClick={() => setShowMcaPrefill(true)}
            className="swiss-btn-ghost"
            data-testid="mca-prefill-open"
            title="Generate an MCA V3 Filing Cheat Sheet (AOC-4 / MGT-7A) — pre-fills every form field."
          >
            <Upload size={14} /> MCA V3 Prefill
          </button>
          )}
          <Link to={`/calendar?company=${id}`} className="swiss-btn-ghost" data-testid="view-calendar-cta">
            View Calendar
          </Link>
          <Link to={`/companies/${id}/edit`} className="swiss-btn-ghost" data-testid="edit-company-cta">
            <Pencil size={14} /> Edit
          </Link>
          <button onClick={handleDelete} className="swiss-btn-ghost text-red-600 border-red-600 hover:bg-red-600 hover:text-white" data-testid="delete-company">
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </div>

      {/* Company details grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-10">
        <div className="bg-white p-6 lg:col-span-2">
          <div className="label-eyebrow mb-4">Master Data</div>
          <div className="tabular-row"><span>Email</span><span>{company.email || "—"}</span></div>
          <div className="tabular-row"><span>Incorporation Date</span><span>{formatIndianDate(company.incorporation_date) || "—"}</span></div>
          <div className="tabular-row"><span>Authorized Capital</span><span>{company.authorized_capital || "—"}</span></div>
          <div className="tabular-row"><span>Paid-up Capital</span><span>{company.paid_up_capital || "—"}</span></div>
          <div className="tabular-row"><span>Registered Address</span><span>{company.registered_address || "—"}</span></div>
        </div>
        <div className="bg-white p-6">
          <div className="label-eyebrow mb-4 flex items-center gap-2"><Users size={14} /> Directors ({company.directors?.length || 0})</div>
          {(company.directors || []).map((d, i) => (
            <div key={d.din || d.name || `director-${i}`} className="py-3 border-b border-zinc-100 last:border-0" data-testid={`director-${i}`}>
              <div className="font-medium">{d.name}</div>
              <div className="text-xs text-zinc-500 font-mono-plex mt-0.5">DIN: {d.din || "—"}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{d.designation}</div>
              {d.appointment_date && (
                <div className="text-[11px] text-zinc-400 mt-0.5">Appointed: {formatIndianDate(d.appointment_date)}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Members / Shareholders */}
      {(company.members || []).length > 0 && (
        <div className="border border-zinc-200 bg-white p-6 mb-10" data-testid="members-section">
          <div className="label-eyebrow mb-4">Members / Shareholders ({company.members.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {computeShareholdingPercents(company.members).map((m, i) => (
              <div key={m.email || m.name || `member-${i}`} className="border border-zinc-200 p-4" data-testid={`member-${i}`}>
                <div className="font-medium">{m.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{m.email || "—"}</div>
                <div className="flex justify-between mt-3 text-xs">
                  <span className="text-zinc-500">{m.member_type}</span>
                  <span className="font-mono-plex">{m.shares_held || 0} shares · {m.shareholding_percent}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Professionals */}
      {(company.professionals || []).length > 0 && (
        <div className="border border-zinc-200 bg-white p-6 mb-10" data-testid="professionals-section">
          <div className="label-eyebrow mb-4 flex items-center gap-2"><Briefcase size={13} className="text-[#002FA7]" /> Professionals ({company.professionals.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {company.professionals.map((p, i) => (
              <div key={p.membership_no || p.email || p.name || `professional-${i}`} className="border border-zinc-200 p-4" data-testid={`professional-${i}`}>
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="font-medium">{p.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#002FA7]">{p.role}</span>
                </div>
                {p.firm_name && <div className="text-xs text-zinc-700 mt-0.5">{p.firm_name}</div>}
                <div className="text-xs text-zinc-500 mt-1 font-mono-plex space-y-0.5">
                  {p.membership_no && <div>Mem #: {p.membership_no}</div>}
                  {p.firm_registration_no && <div>FRN: {p.firm_registration_no}</div>}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{p.email || ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolutions */}
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight mb-4">Resolutions</h2>
        {resolutions.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-10 text-center" data-testid="no-resolutions">
            <FileText size={28} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-3" />
            <p className="text-zinc-600 mb-4">No resolutions yet for this company.</p>
            <Link to={`/generate?company=${id}`} className="swiss-btn-primary" data-testid="generate-first-resolution">
              <Sparkles size={14} /> Generate First Resolution
            </Link>
          </div>
        ) : (
          <div className="border border-zinc-200">
            {resolutions.map((r, i) => (
              <Link
                to={`/resolutions/${r.id}`}
                key={r.id}
                className={`flex items-center justify-between p-4 ${i > 0 ? "border-t border-zinc-100" : ""} hover:bg-zinc-50`}
                data-testid={`resolution-row-${r.id}`}
              >
                <div>
                  <div className="font-medium">{r.resolution_title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    <span className="font-mono-plex">{r.resolution_number}</span> · {formatIndianDate(r.meeting_date)}
                  </div>
                </div>
                {r.is_special_resolution && (
                  <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#002FA7] text-white">Special</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {showMcaPrefill && (
        <McaPrefillModal company={company} onClose={() => setShowMcaPrefill(false)} />
      )}
    </div>
  );
}
